import { Badge } from "@repo/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/table";
import { useQuery } from "@tanstack/react-query";
import { Globe, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchSuburbProfileAdapters, type SuburbProfileAdapter } from "@/lib/api";
import { formatDate } from "@/lib/formatDate";

const SuburbAdapters = () => {
  const { data: adapters, isLoading, error } = useQuery<SuburbProfileAdapter[]>({
    queryKey: ["suburb-profile-adapters"],
    queryFn: fetchSuburbProfileAdapters,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="w-6 h-6" />
            Suburb Adapters
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Sources that scrape suburb-level macro stats (median prices, growth, rental yield)
            monthly. Separate from the property scrapers in the Adapters page.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Registered adapters</CardTitle>
          <CardDescription>
            One adapter per upstream data source. Click a row to open its detail page with run
            history and a manual scrape trigger.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading adapters…
            </div>
          ) : error ? (
            <p className="text-sm text-destructive py-8">
              Failed to load adapters: {(error as Error).message}
            </p>
          ) : !adapters || adapters.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8">
              No suburb adapters registered yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Base URL</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adapters.map((adapter) => (
                  <TableRow key={adapter.id} className="hover:bg-muted/50">
                    <TableCell>
                      <Link
                        to={`/dashboard/suburb-adapters/${adapter.slug}`}
                        className="font-medium hover:underline"
                      >
                        {adapter.name}
                      </Link>
                      {adapter.description && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {adapter.description}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <code className="text-xs">{adapter.slug}</code>
                    </TableCell>
                    <TableCell>
                      <a
                        href={adapter.base_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-muted-foreground hover:underline"
                      >
                        {adapter.base_url}
                      </a>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          adapter.status === "active"
                            ? "bg-success/15 text-success border-success/30"
                            : "bg-muted text-muted-foreground border-muted"
                        }
                      >
                        {adapter.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(adapter.updated_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Schedule</CardTitle>
          <CardDescription>
            Suburb adapters run monthly via pg_cron at 02:00 UTC on the 1st of each month. Manual
            runs are also available from each adapter's detail page.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
};

export default SuburbAdapters;
